// ============================================
// Fallback Performance Tab — Real-Time Monitor
//
// Shows live failover state, performance snapshots,
// and active fallback-related alerts. Uses Firestore
// onSnapshot for real-time updates.
//
// Reads from:
//   _ops/model_degradation/failover_state
//   _ops/model_degradation/performance_snapshots/*
//   _ops/alerts/active/* (filtered to fallback sources)
// ============================================

import { useState, useEffect } from 'react';
import { doc, collection, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { C_OPS } from '../../../constants/collections';

// ── Design tokens ──

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
};

// ── Types ──

interface FailoverState {
  active: boolean;
  fallbackTarget: string | null;
  activatedAt: string | null;
  reason: string | null;
}

interface PerformanceSnapshot {
  timestamp: string;
  windowMinutes: number;
  totalRequests: number;
  successCount: number;
  errorCount: number;
  errorRate: number;
  avgLatencyMs: number;
  p95LatencyMs: number;
  maxLatencyMs: number;
  skillBreakdown: Record<string, { count: number; errors: number; avgLatencyMs: number }>;
  failoverActiveSinceHours: number | null;
  alertsTriggered: string[];
}

interface FallbackAlert {
  id: string;
  severity: string;
  title: string;
  description: string;
  source: string;
  detectedAt: string;
}

// ── Component ──

export function FallbackTab() {
  const [failoverState, setFailoverState] = useState<FailoverState | null>(null);
  const [snapshots, setSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [alerts, setAlerts] = useState<FallbackAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubs: (() => void)[] = [];

    // 1. Real-time failover state
    const failoverRef = doc(db, C_OPS, 'model_degradation', 'failover_state');
    unsubs.push(
      onSnapshot(failoverRef, (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setFailoverState({
            active: Boolean(d.active),
            fallbackTarget: d.fallbackTarget || null,
            activatedAt: d.activatedAt || null,
            reason: d.reason || null,
          });
        } else {
          setFailoverState({ active: false, fallbackTarget: null, activatedAt: null, reason: null });
        }
        setLoading(false);
      }, () => {
        setFailoverState({ active: false, fallbackTarget: null, activatedAt: null, reason: null });
        setLoading(false);
      }),
    );

    // 2. Recent performance snapshots (last 20)
    const snapshotsRef = collection(db, C_OPS, 'model_degradation', 'performance_snapshots');
    const snapshotsQuery = query(snapshotsRef, orderBy('timestamp', 'desc'), limit(20));
    unsubs.push(
      onSnapshot(snapshotsQuery, (snap) => {
        setSnapshots(snap.docs.map((d) => d.data() as PerformanceSnapshot).reverse());
      }, (err) => {
        console.warn('[FallbackTab] snapshots listener error:', err.message);
      }),
    );

    // 3. Active fallback-related alerts
    const alertsRef = collection(db, C_OPS, 'alerts', 'active');
    const alertsQuery = query(alertsRef, where('source', '>=', 'fallbackPerformanceMonitor'), where('source', '<=', 'fallbackPerformanceMonitor\uf8ff'));
    unsubs.push(
      onSnapshot(alertsQuery, (snap) => {
        setAlerts(snap.docs.map((d) => ({ id: d.id, ...d.data() } as FallbackAlert)));
      }, (err) => {
        console.warn('[FallbackTab] alerts listener error:', err.message);
      }),
    );

    return () => unsubs.forEach((u) => u());
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 14 }}>
        Loading fallback monitoring data...
      </div>
    );
  }

  const isActive = failoverState?.active ?? false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Failover Status Banner */}
      <div style={{
        background: isActive ? C.failBg : C.passBg,
        border: `1px solid ${isActive ? C.fail : C.pass}`,
        borderRadius: 4,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: isActive ? C.fail : C.pass,
            animation: isActive ? 'pulse 1.5s infinite' : undefined,
          }} />
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
              {isActive ? 'Cloud Fallback ACTIVE' : 'Primary Model Operational'}
            </p>
            {isActive && failoverState?.reason && (
              <p style={{ fontSize: 13, color: C.textSec, marginTop: 2 }}>
                Reason: {failoverState.reason}
              </p>
            )}
          </div>
        </div>
        {isActive && failoverState?.activatedAt && (
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 12, color: C.textMuted }}>Active since</p>
            <p style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.text }}>
              {formatTimestamp(failoverState.activatedAt)}
            </p>
            {failoverState.fallbackTarget && (
              <p style={{ fontSize: 12, color: C.textSec, marginTop: 2 }}>
                Target: {failoverState.fallbackTarget}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 }}>
            Active Fallback Alerts ({alerts.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alerts.map((a) => (
              <div key={a.id} style={{
                padding: '10px 14px',
                borderRadius: 4,
                backgroundColor: a.severity === 'P0' ? C.failBg : C.warnBg,
                border: `1px solid ${a.severity === 'P0' ? C.fail : C.warn}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: a.severity === 'P0' ? C.fail : C.warn,
                    padding: '1px 6px',
                    borderRadius: 4,
                  }}>
                    {a.severity}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{a.title}</span>
                </div>
                <p style={{ fontSize: 12, color: C.textSec, lineHeight: 1.4 }}>
                  {a.description.slice(0, 200)}{a.description.length > 200 ? '...' : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {snapshots.length > 0 ? (
        <>
          {/* Latest snapshot summary */}
          <LatestSnapshotCards snapshot={snapshots[snapshots.length - 1]} />

          {/* Trend chart */}
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 16 }}>
              Performance Over Time ({snapshots.length} snapshots)
            </p>
            <SnapshotChart snapshots={snapshots} />
          </div>

          {/* Snapshot history table */}
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 }}>
              Recent Snapshots
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: C.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>Time</th>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>Requests</th>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>Error Rate</th>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>Avg Latency</th>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>P95 Latency</th>
                    <th style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>Alerts</th>
                  </tr>
                </thead>
                <tbody>
                  {[...snapshots].reverse().map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                        {formatTimestamp(s.timestamp)}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{s.totalRequests}</td>
                      <td style={{ padding: '8px 12px', color: s.errorRate > 0.25 ? C.fail : s.errorRate > 0.1 ? C.warn : C.pass, fontWeight: 600 }}>
                        {Math.round(s.errorRate * 100)}%
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace" }}>
                        {s.avgLatencyMs > 0 ? `${(s.avgLatencyMs / 1000).toFixed(1)}s` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace" }}>
                        {s.p95LatencyMs > 0 ? `${(s.p95LatencyMs / 1000).toFixed(1)}s` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {s.alertsTriggered.length > 0 ? (
                          <span style={{ color: C.fail, fontWeight: 600 }}>{s.alertsTriggered.length}</span>
                        ) : (
                          <span style={{ color: C.textMuted }}>0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div style={{
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: 40,
          textAlign: 'center',
        }}>
          <p style={{ fontSize: 14, color: C.textSec }}>
            {isActive
              ? 'Fallback is active but no performance snapshots recorded yet. The monitor runs every 15 minutes.'
              : 'No performance data available. Snapshots are recorded when cloud fallback is active.'}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Latest Snapshot Cards ──

function LatestSnapshotCards({ snapshot }: { snapshot: PerformanceSnapshot }) {
  const cards = [
    {
      label: 'Requests (15min)',
      value: String(snapshot.totalRequests),
      sub: `${snapshot.successCount} ok / ${snapshot.errorCount} errors`,
      color: snapshot.totalRequests === 0 ? C.textMuted : C.text,
    },
    {
      label: 'Error Rate',
      value: `${Math.round(snapshot.errorRate * 100)}%`,
      sub: snapshot.errorRate > 0.25 ? 'Above 25% threshold' : 'Within threshold',
      color: snapshot.errorRate > 0.25 ? C.fail : snapshot.errorRate > 0.1 ? C.warn : C.pass,
    },
    {
      label: 'Avg Latency',
      value: snapshot.avgLatencyMs > 0 ? `${(snapshot.avgLatencyMs / 1000).toFixed(1)}s` : '—',
      sub: snapshot.avgLatencyMs > 45000 ? 'Above 45s threshold' : 'Within threshold',
      color: snapshot.avgLatencyMs > 45000 ? C.fail : snapshot.avgLatencyMs > 30000 ? C.warn : C.pass,
    },
    {
      label: 'Failover Duration',
      value: snapshot.failoverActiveSinceHours !== null ? `${snapshot.failoverActiveSinceHours}h` : '—',
      sub: (snapshot.failoverActiveSinceHours ?? 0) > 24 ? 'Over 24h threshold' : 'Within threshold',
      color: (snapshot.failoverActiveSinceHours ?? 0) > 24 ? C.warn : C.pass,
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
      {cards.map((c) => (
        <div key={c.label} style={{
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: 16,
        }}>
          <p style={{ fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            {c.label}
          </p>
          <p style={{ fontSize: 24, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: c.color }}>
            {c.value}
          </p>
          <p style={{ fontSize: 12, color: C.textSec, marginTop: 4 }}>{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ── Simple Bar Chart ──

function SnapshotChart({ snapshots }: { snapshots: PerformanceSnapshot[] }) {
  if (snapshots.length === 0) return null;

  const maxRequests = Math.max(...snapshots.map((s) => s.totalRequests), 1);
  const barWidth = Math.max(12, Math.floor(600 / snapshots.length) - 4);

  return (
    <div>
      {/* Error rate + request count bars */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100, marginBottom: 8 }}>
        {snapshots.map((s, i) => {
          const height = Math.max(2, (s.totalRequests / maxRequests) * 90);
          const errorHeight = s.totalRequests > 0 ? (s.errorCount / s.totalRequests) * height : 0;
          const successHeight = height - errorHeight;

          return (
            <div
              key={i}
              style={{ width: barWidth, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
              title={`${formatTimestamp(s.timestamp)}\n${s.totalRequests} requests, ${Math.round(s.errorRate * 100)}% errors, ${(s.avgLatencyMs / 1000).toFixed(1)}s avg`}
            >
              {errorHeight > 0 && (
                <div style={{ height: errorHeight, backgroundColor: C.fail, borderRadius: '2px 2px 0 0' }} />
              )}
              <div style={{ height: successHeight, backgroundColor: C.pass, borderRadius: errorHeight > 0 ? 0 : '2px 2px 0 0' }} />
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: C.pass }} />
          <span style={{ fontSize: 12, color: C.textSec }}>Success</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: C.fail }} />
          <span style={{ fontSize: 12, color: C.textSec }}>Errors</span>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
